import { ClockClockwiseIcon, InfoIcon, ShieldCheckIcon } from '@phosphor-icons/react';

import { ActionMenu, type ActionMenuGroup } from '@/components/common/action-menu';
import { BackButton } from '@/components/common/back-button';
import { formatLastSync } from '@/lib/date';

import { SUBSCRIPTION_INDICATOR, type DeviceDetails } from '../details-schemas';
import type { Device } from '../types';

import styles from './device-details-header.module.scss';
import { DeviceStatusChip } from './device-status-chip';

export type DeviceDetailsHeaderProps = {
  details: DeviceDetails;
  /** The details payload adapted to the listing's shape, for the shared status chip. */
  device: Device;
  menuGroups: ActionMenuGroup[];
  /** Scrolls to the metadata card — what the Info icon does when expiring within 30 days. */
  onIndicatorClick?: () => void;
};

/**
 * The Device Details header (US-112437 §1): back link, the device's name with its subscription
 * indicator, the default/serial name beside the status chip and last-sync, and the overflow menu.
 *
 * A **shield** means the subscription is comfortably active; an **info icon** replaces it when the
 * subscription expires within 30 days, and clicking that scrolls to the metadata card where the
 * plan field explains why. An unrecognised indicator shows no badge at all.
 */
export function DeviceDetailsHeader({
  details,
  device,
  menuGroups,
  onIndicatorClick,
}: DeviceDetailsHeaderProps) {
  const lastSync = formatLastSync(details.lastSyncedAt);
  const indicator = details.subscriptionIndicator;

  return (
    <header className={styles.header}>
      <div className={styles.topRow}>
        <BackButton label="Back" />
        <ActionMenu groups={menuGroups} triggerLabel="Device actions" />
      </div>

      <div className={styles.identity}>
        <h1 className={styles.name}>
          {details.customName?.trim() ? details.customName : details.defaultName}
          {indicator === SUBSCRIPTION_INDICATOR.PROTECTED ? (
            <span className={styles.shield} title="Subscription active">
              <ShieldCheckIcon size={18} weight="fill" aria-label="Subscription active" />
            </span>
          ) : null}
          {indicator === SUBSCRIPTION_INDICATOR.EXPIRING_SOON ||
          indicator === SUBSCRIPTION_INDICATOR.EXPIRED ? (
            <button
              type="button"
              className={styles.info}
              onClick={onIndicatorClick}
              aria-label="Subscription expiring — show plan details"
            >
              <InfoIcon size={18} weight="fill" />
            </button>
          ) : null}
        </h1>

        <div className={styles.subRow}>
          {/*
            The default name sits under the title only when a custom name has actually taken it
            over. Real accounts do set `customName` to the same string as `defaultName`, which
            would otherwise print the device's name twice.
          */}
          {details.customName?.trim() &&
          details.customName.trim() !== details.defaultName.trim() ? (
            <span className={styles.serial}>{details.defaultName}</span>
          ) : null}
          <DeviceStatusChip device={device} />
          {lastSync ? (
            <span className={styles.lastSync}>
              <ClockClockwiseIcon size={12} aria-hidden />
              Last Sync {lastSync}
            </span>
          ) : null}
        </div>
      </div>
    </header>
  );
}
